import React, {JSX, useState, useEffect, ChangeEvent, startTransition} from 'react';
import {
    useGetList
} from 'react-admin';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    TextField,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Operator } from "../../../../../../types/entities";
import { useEmailFinderActivityPanelManager } from "./hooks/email-finder-activity-panel";
import { StatusChip } from "../../email-finder/components/StatusChip";
import { isCompleteStatus } from "../../email-finder/email-finder-format";

export function mergeStatusUpdates(
    prev: any[],
    updates: any[]
): any[] {
    // If lengths differ, positional merge is unsafe. Replace or merge defensively.
    if (prev.length !== updates.length) {
        return updates; // simplest, consistent result
    }

    let changed: boolean = false;

    const next: any[] = prev.map( (row: any, i: number): any => {
        const u: any = updates[i];

        let rowOne = row[0]
        if (u[0] !== rowOne) {
            rowOne = u[0];
            changed = true;
        }
        let rowTwo = row[1]
        if (u[1] !== rowTwo) {
            rowTwo = u[1];
            changed = true;
        }
        let rowThree = row[2]
        if (u[2] !== rowThree) {
            rowThree = u[2];
            changed = true;
        }

        return [rowOne, rowTwo, rowThree, row[3]];
    });
    return changed ? next : prev; // if nothing changed, keep same array reference
}

/**
 * The number of rows the operator's activity endpoint returns per page.
 * `fetch_activity` in `scraper_database.py` hard codes `page_size = 30`, and
 * every page figure on this panel has to be read from the same number or the
 * buttons and the label disagree. HQ-18.
 */
export const ACTIVITY_PAGE_SIZE: number = 30;

/**
 * How many pages the current row count covers. An empty result reads as one
 * page rather than as page one of none.
 */
export function activityPageCount(totalActivity: number): number {
    return Math.max(1, Math.ceil(totalActivity / ACTIVITY_PAGE_SIZE));
}

/** The label the panel shows for each of the operator's own status values. */
export function statusLabel(status: string): string {
    switch (status) {
        case 'in_progress':
            return 'In Progress';
        case 'pending':
            return 'Pending';
        case 'complete':
        case 'completed':
            return 'Completed';
        default:
            return status || 'N/A';
    }
}

/**
 * The rows the table shows. A row is `[id, url, status, createdAt]`, and the
 * Hide Complete Status box drops the finished ones under either of the two
 * words the operator writes for them. HQ-19.
 */
export function filterActivityRows(rows: any[], hideComplete: boolean): any[] {
    if (!hideComplete) {
        return rows;
    }

    return rows.filter((row: any): boolean => !isCompleteStatus(row[2]));
}

export const EmailFinderActivityPanel: () => JSX.Element = (): JSX.Element => {
    const [selectedOperator, setSelectedOperator] = useState<number|string>('');
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [totalActivity, setTotalActivity] = useState<number>(0);
    const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
    const [activeOperator, setActiveOperator] = useState<Operator>();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [activeTableData, setActiveTableData] = useState<any>([]);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [hideComplete, setHideComplete] = useState<boolean>(true);
    const { loadingData, loadData, dataError, tableData } = useEmailFinderActivityPanelManager();
    const { data, total, isPending, error } = useGetList(
        'operators',
        {
            pagination: { page: 1, perPage: 20 },
            sort: { field: 'id', order: 'ASC' }
        }
    );

    function handleHideCompleteChange(e: ChangeEvent<HTMLInputElement>): void {
        setHideComplete(e.target.checked)
    }

    function handleSearchTermChange(e: ChangeEvent<HTMLInputElement>): void {
        let searchTerm: string = e.target.value;
        setSearchTerm(searchTerm);
        let tm: ReturnType<typeof setTimeout> = setTimeout( (): void => {
            setLoadingSearch(true);
            setPageNumber(1)
        }, 1000 );
        clearTimeout(tm);
    }

    async function handleOperatorChange(event: any): Promise<void> {
        let id: number = parseInt(event.target.value);
        setSelectedOperator(id);
        let operator: Operator;
        for (const datum of data) {
            if (datum.id === id) {
                operator = datum;
            }
        }
        if (operator) {
            setActiveOperator(operator);
            await loadData(operator, searchTerm);
        }
    }

    function handleNextPage(): void {
        let num: number = pageNumber;
        // debugger;
        let totalPages: number = activityPageCount(totalActivity);
        let newPageNumber: number = num + 1;
        if (newPageNumber <= totalPages) {
            setPageNumber(newPageNumber);
            setLoadingSearch(true);
        }



    }

    function handlePreviousPage(): void {
        let num: number = pageNumber;
        let newPageNumber: number = num - 1;
        if (newPageNumber >= 1) {
            setPageNumber(newPageNumber);
            setLoadingSearch(true);
        }


    }

    useEffect( (): any => {
        if (!data || total <= 0 || data.length === 0) {
            setIsPolling(false);
            return;
        }

        if (!isPending && total > 0 && selectedOperator === "") {
            let firstId: number = parseInt(data[0].id);
            let firstOperator: Operator = data[0];
            setSelectedOperator(firstId);
            setActiveOperator(firstOperator)
            loadData(firstOperator, searchTerm, pageNumber)
                .then( async (data: any): Promise<void> => {
                    if (data !== undefined && typeof data['message'] !== 'undefined') {
                        let message: string = data['message'];
                        message = JSON.parse(message);
                        if (typeof message !== "undefined" &&
                            typeof message['response'] !== 'undefined' &&
                            message['response'] === 'success'
                        ) {
                            setTotalActivity(message['data']['total_rows'] ?? 0);
                            setActiveTableData(message['data']['results']);
                        }
                    }
                } )
        }

        // debugger;
        let cancelled: boolean = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const POLL_INTERVAL_MS = 3000;

        async function start(): Promise<void> {
            setIsPolling(true);
            const pollLoop: () => Promise<void> = async (): Promise<void> => {
                if (cancelled) return;
                try {
                    if (activeOperator === undefined || tableData === undefined) {
                        return;
                    }
                    await loadData(activeOperator, searchTerm, pageNumber)
                        .then( async (data: any): Promise<void> => {
                            if (typeof data['message'] !== 'undefined') {
                                let message: string = data['message'];
                                message = JSON.parse(message);
                                if (typeof message !== "undefined" &&
                                    typeof message['response'] !== 'undefined' &&
                                    message['response'] === 'success'
                                ) {
                                    startTransition( (): void => {
                                        setActiveTableData( (prev: any): any[] => mergeStatusUpdates(prev, message['data']['results']) );
                                        if (loadingSearch) {
                                            setLoadingSearch(false);
                                        }
                                        setTotalActivity(message['data']['total_rows'] ?? 0);
                                    } )
                                }
                            }
                        } )
                    // schedule next tick (setTimeout avoids overlapping async calls)
                    timer = setTimeout(pollLoop, POLL_INTERVAL_MS);
                } catch {
                    if (timer) {
                        clearTimeout(timer);
                    }
                }
            };
            // Kick off first poll tick
            timer = setTimeout(pollLoop, POLL_INTERVAL_MS);
        }
        start();

        return (): void => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };

    }, [data, total, selectedOperator, loadData, searchTerm, isPending, activeOperator, tableData, loadingSearch, pageNumber] );

    // One figure, read by the label, both buttons and the next page handler.
    // They used to carry three copies of it and two different page sizes.
    const pageCount: number = activityPageCount(totalActivity);
    const visibleRows: any[] = filterActivityRows(activeTableData, hideComplete);

    return (
        <Box sx={ { width: '100%' } }>
            <Box sx={ { width: '100%', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' } } >
                { (isPending) &&
                    <Typography variant="body2" sx={ { color: 'text.secondary' } }>{ "Loading Operators..." }</Typography>
                }
                { (!isPending && total > 0) &&
                    <FormControl size="small" sx={ { flex: '0 0 auto', width: '240px' } }>
                        <InputLabel id="email-finder-activity-operator">{ "Operator" }</InputLabel>
                        <Select labelId="email-finder-activity-operator"
                                label="Operator"
                                value={ selectedOperator }
                                onChange={ handleOperatorChange } >
                            { data?.map( (operator: Operator, index: number): JSX.Element => {
                                return (
                                    <MenuItem key={ "operator_" + index } value={ parseInt(operator.id) } >{ operator.name }</MenuItem>
                                );
                            } ) }
                        </Select>
                    </FormControl>
                }
                { (!isPending && total <= 0) &&
                    <Typography variant="body2" sx={ { color: 'text.secondary' } }>{ "No Operators Found" }</Typography>
                }
            </Box>

            <Box sx={ {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                margin: '24px 0 12px 0',
            } } >
                <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } } >
                    <TextField variant="outlined"
                               size="small"
                               value={ searchTerm }
                               onChange={ handleSearchTermChange }
                               placeholder='Search Urls'
                               slotProps={ {
                                   input: {
                                       startAdornment: (
                                           <InputAdornment position="start">
                                               <SearchIcon fontSize="small" />
                                           </InputAdornment>
                                       ),
                                   },
                               } }
                               sx={ { width: '300px' } } />
                    <FormControlLabel label="Hide Complete Status"
                                      control={
                                          <Checkbox value={ hideComplete }
                                                    checked={ hideComplete }
                                                    onChange={ handleHideCompleteChange } />
                                      } />
                </Box>
                <Box sx={ { display: 'flex', alignItems: 'center', gap: 2 } }>
                    <Typography variant="body2" sx={ { color: 'text.secondary' } } >
                        { `Urls currently being processed by ${activeOperator?.name}: ${totalActivity}` }
                    </Typography>
                    <Box sx={ { display: 'flex', alignItems: 'center', gap: 1 } }>
                        <IconButton size="small"
                                    aria-label="Previous page"
                                    disabled={ (pageNumber <= 1) || loadingSearch }
                                    onClick={ handlePreviousPage } >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={ { minWidth: '52px', textAlign: 'center' } } >
                            { `${pageNumber} / ${pageCount}` }
                        </Typography>
                        <IconButton size="small"
                                    aria-label="Next page"
                                    onClick={ handleNextPage }
                                    disabled={ (pageNumber >= pageCount) || loadingSearch } >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <Box sx={ { width: '100%', height: '500px', position: 'relative' } } >
                { (loadingSearch) &&
                    <Box sx={ {
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        zIndex: 2,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    } } >
                        <CircularProgress size={ 40 } />
                    </Box>
                }
                <TableContainer sx={ {
                    height: '500px',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                } } >
                    {/* Fixed layout so the columns divide the width the panel has.
                        Left to size itself, one long url reports its width up through
                        react-admin's fit-content layout and widens the whole admin. */}
                    <Table stickyHeader size="small" sx={ { tableLayout: 'fixed', width: '100%' } }>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={ { width: '90px' } }>Id</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell align="center" sx={ { width: '180px' } }>Created</TableCell>
                                <TableCell align="right" sx={ { width: '160px' } }>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { visibleRows.map((row: any): JSX.Element => {
                                const statusParam: string = row[2] ?? '';

                                return (
                                    <TableRow
                                        key={ row[0] }
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell align="left">
                                            { row[0] }
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={ {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            } }>
                                                <a href={ row[1] } target="_blank" rel="noreferrer" title={ row[1] }>
                                                    { row[1] }
                                                </a>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">{ row[3] || 'N/A' }</TableCell>
                                        <TableCell align="right">
                                            <StatusChip status={ statusParam } label={ statusLabel(statusParam) } dense />
                                        </TableCell>
                                    </TableRow>
                                );
                            } ) }
                            { (visibleRows.length === 0) &&
                                <TableRow>
                                    <TableCell colSpan={ 4 } align="center" sx={ { border: 0, padding: '48px 0' } }>
                                        <Typography variant="body2" sx={ { color: 'text.secondary' } }>
                                            { loadingData ? "Loading URLs" : "No URLs to show" }
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}
